import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { AlertCircle, CheckCircle, Shield } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const ResetPassword = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [hasAccessToken, setHasAccessToken] = useState(false);

  useEffect(() => {
    const tryRecoverSession = async () => {
      console.log("Password Reset (window.location.search): ", window.location.search);
      console.log("Password Reset (window.location.hash): ", window.location.hash);
      const hash = window.location.hash.substring(1); // remove the leading #
      const params = new URLSearchParams(hash);
      const access_token = params.get("access_token");
      const type = params.get("type");

      console.log("Token:", access_token, "Type:", type);

      if (access_token && type === "recovery") {
        const { data, error } = await supabase.auth.exchangeCodeForSession(access_token);
        if (error) {
          console.error("Session exchange error:", error);
          setError("Failed to verify session.");
        } else {
          console.log("Session established:", data.session);
          setHasAccessToken(true);
          window.history.replaceState({}, document.title, "/reset-password");
        }
      } else {
        const { data } = await supabase.auth.getSession();
        if (data.session) {
          console.log("User already logged in.");
          setHasAccessToken(true);
        } else {
          setError("Invalid or expired password reset link. Please request a new one.");
        }
      }
    };

    tryRecoverSession();
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    // Check if passwords match
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      setIsLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setError("Password should be at least 6 characters long");
      setIsLoading(false);
      return;
    }

    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;

      setSuccess(true);
      toast({
        title: "Password updated successfully",
        description: "You can now log in with your new password",
      });

      setTimeout(async () => {
        await supabase.auth.signOut();
        navigate("/auth");
      }, 3000);
    } catch (err: any) {
      console.error("Password reset error:", err);
      const message = err?.message || "Failed to update password";
      setError(message);
      toast({
        title: "Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <div className="min-h-screen relative">
        <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                  'url("/lovable-uploads/7f72d2f9-18ce-48a5-8cb7-7c34071c5400.png")',
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
        />
        <div className="relative z-10 min-h-screen bg-gradient-to-b from-hero-dark/80 to-hero-mid/80">
          <Header />
          <div className="container mx-auto px-4 flex items-center justify-center min-h-[calc(100vh-8rem)]">
            <Card className="w-full max-w-md mx-auto relative z-10 bg-white/95 backdrop-blur-md shadow-xl">
              <CardHeader className="text-center space-y-2">
                <CardTitle className="text-2xl font-bold text-[#122D44]">
                  Reset Your Password
                </CardTitle>
                <CardDescription className="text-base text-gray-700">
                  Enter your new password below
                </CardDescription>
              </CardHeader>
              <CardContent>
                {error && (
                    <Alert variant="destructive" className="mb-4">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {success && (
                    <Alert className="bg-green-50 border-green-200 mb-4">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <AlertDescription className="text-green-700">
                        Password updated successfully! Redirecting to login page...
                      </AlertDescription>
                    </Alert>
                )}

                {hasAccessToken ? (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <label
                            htmlFor="password"
                            className="text-sm font-medium text-gray-700"
                        >
                          New Password
                        </label>
                        <Input
                            id="password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Enter new password"
                            required
                            disabled={isLoading || success}
                            className="w-full"
                            minLength={6}
                        />
                      </div>

                      <div className="space-y-2">
                        <label
                            htmlFor="confirmPassword"
                            className="text-sm font-medium text-gray-700"
                        >
                          Confirm New Password
                        </label>
                        <Input
                            id="confirmPassword"
                            type="password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm new password"
                            required
                            disabled={isLoading || success}
                            className="w-full"
                            minLength={6}
                        />
                      </div>

                      <Button
                          type="submit"
                          className="w-full h-12 bg-[#FFC300] hover:bg-[#FFC300]/90 text-gray-900 font-semibold rounded-full shadow-lg hover:shadow-xl transition-all duration-200"
                          disabled={isLoading || success}
                      >
                        {isLoading ? "Updating..." : "Reset Password"}
                      </Button>
                    </form>
                ) : (
                    <div className="text-center p-4">
                      <Button
                          onClick={() => navigate("/auth")}
                          className="mt-4 bg-[#FFC300] hover:bg-[#FFC300]/90 text-gray-900"
                      >
                        Return to Login
                      </Button>
                    </div>
                )}

                <div className="flex items-center justify-center space-x-2 text-sm text-gray-600 mt-4">
                  <Shield className="h-4 w-4" />
                  <span>Secure & Private – Your data is protected</span>
                </div>
              </CardContent>
            </Card>
          </div>
          <Footer />
        </div>
      </div>
  );
};

export default ResetPassword;
